import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import style from './CarouselStandardNews.module.scss';
import Slider from 'react-slick';
import { SmartLink } from '../SmartLink';

const CarouselStandardNews = props => {
    const { title, secondaryText, className, sliderBreakpoints = undefined, sliderParams = undefined, cta, ctaCallback, children } = props;
    const hasSecondaryTextContent = typeof secondaryText === 'string' &&
        secondaryText
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim().length > 0;
    if (children.length <= 0) {
        return <></>;
    }

    const sliderEl = useRef(null);
    const paginationRef = useRef(null);
    const ctaRef = useRef(null);
    const wrapperRef = useRef(null);
    const [prevDisabled, setPrevDisabled] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isClient, setIsClient] = useState(false);
    const [ctaNeedsCompactStyle, setCtaNeedsCompactStyle] = useState(false);

    let activeSliderParams;
    if (sliderParams) {
        activeSliderParams = {
            ...sliderParams,
            responsive: [...sliderBreakpoints],
            dots: false,
            arrows: false,
            swipe: true,
            swipeToSlide: true,
            touchMove: true,
            draggable: true,
            touchThreshold: 10,
            edgeFriction: 0,
            waitForAnimate: false
        };
        activeSliderParams.afterChange = (index) => {
            setArrows(index);
            setCurrentSlide(index);
        };
    } else {
        activeSliderParams = {
            dots: false,
            arrows: false,
            swipe: true,
            swipeToSlide: true,
            touchMove: true,
            draggable: true,
            touchThreshold: 10,
            edgeFriction: 0,
            waitForAnimate: false,
            afterChange: (index) => {
                setArrows(index);
                setCurrentSlide(index);
            }
        };
    }

    const setArrows = (index) => {
        if (typeof window === 'undefined') {
            setPrevDisabled(index === 0);
            setNextDisabled(index === children.length - 1);
            return;
        }

        if (!sliderBreakpoints || sliderBreakpoints.length === 0) {
            setPrevDisabled(index === 0);
            setNextDisabled(index === children.length - 1);
            return;
        }

        const slidesBreakpoint = sliderBreakpoints?.toReversed().find(br => window.innerWidth < br.breakpoint);
        const slidesPerPage = slidesBreakpoint?.settings.slidesToShow || sliderParams?.slidesToShow || 1;
        const slidesToScroll = slidesBreakpoint?.settings.slidesToScroll || sliderParams?.slidesToScroll || 1;
        const totalSlides = Math.ceil((children.length - slidesPerPage) / slidesToScroll) + 1;

        setPrevDisabled(index === 0);
        setNextDisabled(index >= totalSlides - 1);
    }

    const slickPrev = () => {
        sliderEl.current.slickPrev();
    }
    const slickNext = () => {
        sliderEl.current.slickNext();
    }

    const goToSlide = (index) => {
        sliderEl.current.slickGoTo(index);
    }

    // Compute compact pagination items with ellipsised dots
    // Pattern: [first] … [i-2, i-1] [i] [i+1, i+2] … [last]
    // Bounded within [0, total-1]
    const getPaginationItems = () => {
        const total = getTotalSlides();
        const current = currentSlide;
        const items = [];
        // Decide neighbour window by viewport
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        const neighborCount = isMobile ? 1 : 2;

        // Desktop/tablet: show all dots when count is small; otherwise use ellipsis
        if (!isMobile && total < 10) {
            for (let i = 0; i < total; i++) {
                items.push({ type: 'dot', index: i });
            }
            return items;
        }

        // Mobile: cap at max 3 clickable dots
        if (isMobile) {
            if (total <= 5) {
                for (let i = 0; i < total; i++) {
                    items.push({ type: 'dot', index: i });
                }
                return items;
            }

            const start = current <= 1 ? 0 : (current >= total - 2 ? total - 3 : current - 1);
            if (start > 0) items.push({ type: 'ellipsis', key: 'left' });
            for (let i = start; i < start + 3; i++) {
                items.push({ type: 'dot', index: i });
            }
            if (start + 3 < total) items.push({ type: 'ellipsis', key: 'right' });
            return items;
        }

        if (total <= 3 + neighborCount * 2) {
            for (let i = 0; i < total; i++) {
                items.push({ type: 'dot', index: i });
            }
            return items;
        }

        const pushDot = (i) => items.push({ type: 'dot', index: i });
        const pushEllipsis = (key) => items.push({ type: 'ellipsis', key });

        // Leading
        pushDot(0);

        // Left ellipsis window
        if (current > 3) {
            pushEllipsis('left');
        }

        // Neighbours before current
        const start = Math.max(1, current - neighborCount);
        const end = Math.min(total - 2, current + neighborCount);
        for (let i = start; i <= end; i++) {
            pushDot(i);
        }

        // Right ellipsis window
        if (current < total - (2 + neighborCount)) {
            pushEllipsis('right');
        }

        // Trailing
        pushDot(total - 1);

        // Deduplicate contiguous dots around boundaries
        const seen = new Set();
        const normalized = [];
        for (const it of items) {
            const key = it.type === 'dot' ? `d-${it.index}` : `e-${it.key}`;
            if (seen.has(key)) continue;
            seen.add(key);
            normalized.push(it);
        }
        return normalized;
    }

    const getTotalSlides = () => {
        if (typeof window === 'undefined') {
            return 1;
        }

        if (!sliderBreakpoints || sliderBreakpoints.length === 0) {
            return children.length;
        }

        const slidesBreakpoint = sliderBreakpoints?.toReversed().find(br => window.innerWidth < br.breakpoint);
        const slidesPerPage = slidesBreakpoint?.settings.slidesToShow || sliderParams?.slidesToShow || 1;

        // For carousel with slidesToScroll, we need to calculate based on actual slide positions
        // Each slide shows slidesPerPage items, but we need to account for slidesToScroll
        const slidesToScroll = slidesBreakpoint?.settings.slidesToScroll || sliderParams?.slidesToScroll || 1;

        // Calculate how many "steps" we need to show all items
        const totalSteps = Math.ceil((children.length - slidesPerPage) / slidesToScroll) + 1;

        return totalSteps;
    }

    // Check if CTA needs compact style on small screens
    const checkOverlap = () => {
        if (typeof window === 'undefined' || window.innerWidth > 600) {
            setCtaNeedsCompactStyle(false);
            return;
        }

        if (paginationRef.current && ctaRef.current && wrapperRef.current) {
            const wrapperWidth = wrapperRef.current.offsetWidth;
            const paginationWidth = paginationRef.current.offsetWidth;
            const ctaWidth = ctaRef.current.offsetWidth;

            // Calculate available space for CTA
            const availableSpace = (wrapperWidth - paginationWidth) / 2 - 20;

            if (ctaWidth > availableSpace) {
                setCtaNeedsCompactStyle(true);
            } else {
                setCtaNeedsCompactStyle(false);
            }
        }
    };

    useEffect(() => {
        setArrows(0);
        setIsClient(true);

        // Check overlap after component mounts and on resize
        const handleResize = () => {
            setTimeout(checkOverlap, 100); // Small delay to ensure DOM is updated
        };

        window.addEventListener('resize', handleResize);
        setTimeout(checkOverlap, 100); // Initial check

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Check overlap when currentSlide changes (pagination might change size)
    useEffect(() => {
        if (isClient) {
            setTimeout(checkOverlap, 100);
        }
    }, [currentSlide, isClient]);

    return (
        <div className={[style.container, className, 'container'].join(' ')}>
            {title && (
                <div className={style.title}>
                    <h2>{title}</h2>
                </div>
            )}
            {hasSecondaryTextContent && (
                <div className={style.secondaryText} dangerouslySetInnerHTML={{ __html: secondaryText }}></div>
            )}
            <div className={[style.wrapper, 'wrapper'].join(' ')} ref={wrapperRef}>
                <Slider {...activeSliderParams} ref={sliderEl}>
                    {children}
                </Slider>
                <div className={style.slickArrowsAbsolute}>
                    <button className={`${style.slickArrow} ${style.prevBtn} ${prevDisabled && style.disabled}`} onClick={slickPrev}>
                        <img src='/images/news/prev.svg' alt='previous' />
                        <div className='sr-only'>previous</div>
                    </button>
                    <button className={`${style.slickArrow} ${style.nextBtn} ${nextDisabled && style.disabled}`} onClick={slickNext}>
                        <img src='/images/news/next.svg' alt='next' />
                        <div className='sr-only'>next</div>
                    </button>
                </div>
            </div>

            {isClient && (
                <div className={style.pagination} ref={paginationRef}>
                    {getPaginationItems().map((item, idx) => {
                        if (item.type === 'ellipsis') {
                            return (
                                <span
                                    key={`ellipsis-${idx}`}
                                    className={style.ellipsis}
                                    aria-hidden='true'
                                >
                                    …
                                </span>
                            );
                        }
                        return (
                            <button
                                key={`dot-${item.index}`}
                                className={`${style.paginationDot} ${currentSlide === item.index ? style.active : ''}`}
                                onClick={() => goToSlide(item.index)}
                                aria-label={`Go to slide ${item.index + 1}${currentSlide === item.index ? ' (current)' : ''}`}
                            />
                        );
                    })}
                    {(() => {
                        if (typeof window === 'undefined') return null;
                        const isMobile = window.innerWidth < 768;
                        const total = getTotalSlides();
                        if ((isMobile && total > 5) || (!isMobile && total >= 10)) {
                            return (
                                <span className={style.fraction} aria-live='polite' aria-atomic='true'>
                                    {currentSlide + 1}/{total}
                                </span>
                            );
                        }
                        return null;
                    })()}
                </div>
            )}
            {cta?.link?.url && (
                <div className={`${style.cta} ${ctaNeedsCompactStyle ? 'compact' : ''}`}>
                    <div className={style.ctaLink} ref={ctaRef}>
                        <SmartLink href={cta.link.url} className='arrow-link'>
                            {cta.text}
                        </SmartLink>
                    </div>
                </div>
            )}
            {ctaCallback?.onClick && (
                <div className={`${style.cta} ${ctaNeedsCompactStyle ? 'compact' : ''}`}>
                    <div className={style.ctaLink} ref={ctaRef}>
                        <div className={style.ctaCallback} onClick={ctaCallback.onClick}>
                            <a className='arrow-link'>
                                {ctaCallback.text}
                                <span className='icon-arrow-right'></span>
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

CarouselStandardNews.propTypes = {
    title: PropTypes.string,
    className: PropTypes.string,
    secondaryText: PropTypes.string,
    sliderBreakpoints: PropTypes.array,
    sliderParams: PropTypes.object,
    cta: PropTypes.object,
    ctaCallback: PropTypes.object,
    children: PropTypes.array,
};
export default CarouselStandardNews;
CarouselStandardNews.defaultProps = {};
